import { isDefined } from 'ts-utils';
import { getReferralIdSearchParam, isCorrectFormatForReferralId } from '../config/AssessmentUtils';
import { dispatchReferralInfo } from '../store/referral/ReferralActions';
import { INITIAL_REFERRAL_STATE } from '../store/referral/ReferralState';
import { dispatchDoctorUserData } from '../store/userData/UserDataActions';
import { Analytics } from '../util/Analytics';
import { recordError } from '../util/Errors';
import { getReduxReferralState } from './HcpReferrals';

export async function updateReferralStoreStateFromUrl(): Promise<void> {
  /**
   * If a referral ID is present in the query string, we initiate a call in index.tsx to populate the redux store with some referral info.
   * This proceeds asynchronously, and the store.referral.loading is set to false until it completes (either error or success).
   * Descendant components whose behaviour depends on values in `store.referral` can use the store.referral.loading flag to show
   * a spinner while the referral fetch is executing. (e.g. the Onboard component).
   * Note that we do not await this function here, so it proceeds in parallel with experiments initialization.
   * We do however await completion of experiments initialization before rendering the app,
   * as the assessment config is likely to depend on active experiments.
   */

  function getErrorMessage(error: unknown, fallbackMessage?: string): string {
    if (error instanceof Error) return error.message;
    if (fallbackMessage !== undefined) return fallbackMessage;
    return String(error);
  }

  const referralId = getReferralIdSearchParam();
  if (!isDefined(referralId)) {
    dispatchReferralInfo({ ...INITIAL_REFERRAL_STATE });
    return;
  }
  if (isCorrectFormatForReferralId(referralId)) {
    Analytics.track({ event: Analytics.Event.h_hcp_referred });
    dispatchDoctorUserData({ referred: 'Yes' });
    dispatchReferralInfo({ loading: true });
    try {
      const referralInfo = await getReduxReferralState(referralId);
      dispatchReferralInfo({ ...referralInfo, loading: false });
    } catch (error) {
      console.error('Error fetching referral info:', error);
      recordError(error);
      dispatchReferralInfo({
        ...INITIAL_REFERRAL_STATE,
        error: getErrorMessage(error, 'Failed to fetch referral information'),
      });
    }
  } else {
    dispatchReferralInfo({
      ...INITIAL_REFERRAL_STATE,
      error: 'Invalid referral ID format',
    });
    recordError(
      new Error(`Invalid referral id format ignored in query string '${window?.location?.search}'`)
    );
  }
}
